import React, { useCallback, useEffect } from 'react';

import { Divider, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import OrderCancelledModal from 'components/order-cancelled-modal';
import { IDeliveryOrderFragment } from 'generated/graphql-gateway';
import useDialogModal from 'hooks/use-dialog-modal';
import { useEtaCounter } from 'hooks/use-eta-counter/';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import { DeliveryStatus } from 'state/order/types';
import { routes } from 'utils/routing';

import CourierDetail from './courier-detail';
import DeliveryInfo from './delivery-order-card/delivery-info';
import DeliveryOrderDetails from './delivery-order-card/delivery-order-details';
import DeliveryProgress from './delivery-progress';
import { CardHeading, CardHeadingContainer, DeliveryCardContainer } from './styled';

interface IDeliveryDetailsProps {
  delivery: IDeliveryOrderFragment['delivery'];
  serverOrder: IServerOrder;
}

const DeliveryDetails: React.FC<React.PropsWithChildren<IDeliveryDetailsProps>> = ({
  delivery,
  serverOrder,
}) => {
  const { status: deliveryStatus, driver, dropoff } = delivery ?? {};
  const { formatMessage } = useIntl();
  const eta = useEtaCounter(deliveryStatus, dropoff);
  const dismissAndNavigate = useDismissAllAndNavigate();

  // @ts-ignore
  const [CancellationModal, openCancellationModal] = useDialogModal({
    // @ts-expect-error TS(2322) FIXME: Type 'FC<IOrderCancelledModalProps>' is not assign... Remove this comment to see the full error message
    Component: OrderCancelledModal,
    onDismiss: () => dismissAndNavigate(routes.base),
  });

  useEffect(() => {
    if (deliveryStatus) {
      if (deliveryStatus === DeliveryStatus.ORDER_CANCELLED) {
        openCancellationModal();
      }
    }
  }, [deliveryStatus, openCancellationModal, serverOrder]);

  const getDeliveryEtaText = useCallback(
    (status: DeliveryStatus): string => {
      switch (status) {
        case DeliveryStatus.DRIVER_AT_CUSTOMER:
          return formatMessage({ id: 'deliveryDriverArrived' });
        case DeliveryStatus.ORDER_DROPPED_OFF:
          return formatMessage({ id: 'deliveryDelivered' });
        default:
          return eta || '...';
      }
    },
    [eta, formatMessage]
  );

  return (
    <>
      {!!deliveryStatus && (
        <DeliveryCardContainer>
          <CardHeadingContainer focusable>
            <Text>
              {`${formatMessage({ id: 'eta' })}: `}
              <Text testID="order-preparation-confirmation-eta">
                {getDeliveryEtaText(deliveryStatus)}
              </Text>
            </Text>
          </CardHeadingContainer>

          <Divider />

          {!!dropoff && (
            <CardHeadingContainer focusable>
              <CardHeading testID="order-preparation-confirmation" data-private>
                {`${formatMessage({ id: 'deliveringToYourDoorAt' })}\n`}
                {dropoff.addressLine1}
              </CardHeading>
            </CardHeadingContainer>
          )}

          <DeliveryProgress deliveryStatus={deliveryStatus} />

          {!!driver && (
            <CardHeadingContainer>
              <CourierDetail driver={driver} />
            </CardHeadingContainer>
          )}

          <DeliveryInfo serverOrder={serverOrder} />

          <Divider />

          <DeliveryOrderDetails serverOrder={serverOrder} />
        </DeliveryCardContainer>
      )}

      <CancellationModal />
    </>
  );
};

export default DeliveryDetails;
